import { Component, Output, EventEmitter, Input, SimpleChanges, OnChanges, ChangeDetectorRef, OnInit, OnDestroy, HostListener, ViewChild, ElementRef } from "@angular/core";
import { ReportService, ReportAnalysisType } from "../../../services/report.service";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { DocumentData } from "../../../services/document-data.service";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { StylometricStateService } from "../../../services/stylometric-state.service";
import { Subject, Subscription } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

// Define a type for the AI bar analysis state (subset of ReportAnalysisType)
type AnalysisType = 'ai' | 'metadata' | 'stylometrics' | 'projectSynapse';

// Type guard function to check if a string is a valid AnalysisType
function isAnalysisType(value: string): value is AnalysisType {
  return ['ai', 'metadata', 'stylometrics', 'projectSynapse'].includes(value);
}

@Component({
  selector: 'app-ai-bar',
  templateUrl: './ai-bar.component.html',
  styleUrls: ['./ai-bar.component.scss']
})
export class AiBar implements OnChanges, OnInit, OnDestroy {
  title = 'AI Bar'
  @Input() isOnAi: boolean;
  @ViewChild('sectionHeading') sectionHeading?: ElementRef<HTMLElement>;
  @Input() aiAnalysis: any;
  @Input() previousSubmissions: any;
  @Output() isOnAiChange = new EventEmitter<boolean>();
  totalSentences!: number;
  aiSentences!: number;
  humanSentences!: number;
  @Input() totalAIStenteces!: [];
  isAiFolded = true;
  isMetadataFolded = true;
  isStylometricsFolded = true;
  isProjectSynapseFolded = true;
  @Input() metadata!: any;
  @Input() metadataUrl!: string;
  @Output() metadataRefreshRequested = new EventEmitter<void>();
  metadataLength!: number;
  metadataIssues!: number;
  @Input() canEdit: boolean;
  @Input() author!: any;

  @Input() stylometricMetrics: any;
  @Input() metricsThresholds: any;
  @Input() averageStylisticDiscrepancy: string;
  @Input() stylometricsEnable: boolean;
  @Input() projectSynapseEnable: boolean;
  @Input() simpleAIEnable: boolean;
  @Input() metadataEnable: boolean;
  @Input() allSentencesAverage: number;
  @Input() allSentencesLength: number;
  @Input() aiSentenceKeywordsLength: number;
  @Input() projectSynapseData: any;
  @Input() pangramResults: any; // Optional doc-level AI prediction data

  @Input() analysisDate: any;

  @Input() anonymous: boolean = false;

  projectSynapseStylisticDiscrepancy: string = 'low';
  stylometricFlagsCount: number = 0;

  ai_percentage: any = {
    high: '',
    medium: '',
    low: ''
  };
  originalityDisplay: string = '';
  highRiskThreshold: number = 80;
  mediumRiskThreshold: number = 70;

  // For cleanup
  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];

  // Most probable model reference
  private mostProbableModel: any = null;

  constructor(
    private reportService: ReportService,
    private documentDataService: DocumentData,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private stylometricState: StylometricStateService,
    private host: ElementRef<HTMLElement>
  ) { }

  private recalculateMetadataIssues(): void {
    if (!this.metadata) return;

    const isAnonymous = this.anonymous;
    const excludedFlags = this.metadata?.excluded_flags?.map((flag: any) => flag.key) || [];
    if (isAnonymous) {
      excludedFlags.push('author');
    }

    let issuesCount = this.metadata?.flags?.filter((flag: any) =>
      !excludedFlags.includes(flag.key)
    ).length || 0;

    if (!isAnonymous && 'author' in this.metadata && !this.metadata?.flags?.some((f: any) => f.key === 'author')) {
      issuesCount += 1;
    }

    this.metadataIssues = issuesCount;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.countSentences();
    });
  }

  ngOnInit(): void {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const settings = user?.Institution?.InstitutionSettings?.[0];

    this.originalityDisplay = settings?.originalityDisplay || 'Percentage';
    this.highRiskThreshold = settings?.originalityRiskFactorHighRisk || 80;
    this.mediumRiskThreshold = settings?.originalityRiskFactorMediumRisk || 70;
    this.countSentences();
    this.setAiPercentageTranslations();
    this.initializeFoldStates();

    // Calculate initial stylometric flags if data is available
    if (this.stylometricMetrics && this.metricsThresholds && this.previousSubmissions?.all) {
      this.calculateStylometricFlags();
    }

    // Subscribe to language change
    this.subscriptions.push(
      this.translate.onLangChange
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: LangChangeEvent) => {
          this.setAiPercentageTranslations();
        })
    );

    // Subscribe to Project Synapse state
    this.subscriptions.push(
      this.stylometricState.isProjectSynapseActive$
        .pipe(takeUntil(this.destroy$))
        .subscribe(isActive => {
          // Only update if there's a mismatch between the active state and fold state
          if (this.isProjectSynapseFolded === isActive) {
            this.isProjectSynapseFolded = !isActive;

            // Only update other fold states if Project Synapse is becoming active
            // and we're not currently viewing metadata
            if (isActive && this.reportService.selectAnalysisTypeSubject.getValue() !== 'metadata') {
              this.isAiFolded = true;
              this.isMetadataFolded = true;
              this.isStylometricsFolded = true;
            }
            this.cdr.markForCheck();
          }
        })
    );

    // Subscribe to stylometric report state
    this.subscriptions.push(
      this.stylometricState.isStylometricReportOpen$
        .pipe(takeUntil(this.destroy$))
        .subscribe(isOpen => {
          if (isOpen) {
            this.isOnAi = true;
            this.isOnAiChange.emit(true);

            // Only update fold states if we're not currently viewing metadata
            if (this.reportService.selectAnalysisTypeSubject.getValue() !== 'metadata') {
              this.isStylometricsFolded = false;
              this.isAiFolded = true;
              this.isMetadataFolded = true;
            }
          } else {
            // Only reset if we're not currently viewing metadata
            if (this.reportService.selectAnalysisTypeSubject.getValue() !== 'metadata') {
              this.isStylometricsFolded = true;
              this.isAiFolded = true;
              this.isMetadataFolded = true;
              this.isProjectSynapseFolded = true;
              this.stylometricState.toggleProjectSynapseActive(false);
            }
          }
          this.cdr.markForCheck();
        })
    );

    // Subscribe to report type changes
    this.subscriptions.push(
      this.stylometricState.reportType$
        .pipe(takeUntil(this.destroy$))
        .subscribe(type => {
          if (type === 'classic') {
            // Only update fold states if we're not currently viewing metadata
            if (this.reportService.selectAnalysisTypeSubject.getValue() !== 'metadata') {
              this.isAiFolded = true;
              this.isMetadataFolded = true;
              this.isStylometricsFolded = true;
            }
            this.cdr.markForCheck();
          }
        })
    );

    // Subscribe to analysis type changes
    this.subscriptions.push(
      this.reportService.selectAnalysisTypeSubject
        .pipe(takeUntil(this.destroy$))
        .subscribe(analysisType => {
          // No need to update fold states here as they're handled by the specific navigation methods
        })
    );

    // Subscribe to sentence information changes to preserve Project Synapse state
    this.subscriptions.push(
      this.reportService.sentenceInformationObject.pipe(
        takeUntil(this.destroy$)
      ).subscribe((sentenceInfo: any) => {
        // If sentence info is cleared (null) but Project Synapse was active, keep it active
        if (!sentenceInfo && !this.isProjectSynapseFolded) {
          // Small delay to ensure state is preserved after other components update
          setTimeout(() => {
            // Maintain the Project Synapse open state
            this.isProjectSynapseFolded = false;
            this.stylometricState.toggleProjectSynapseActive(true);
            this.cdr.markForCheck();
          }, 50);
        }
      })
    );

    // Add a subscription to the Project Synapse preservation signal
    this.subscriptions.push(
      this.reportService.preserveProjectSynapseState$
        .pipe(takeUntil(this.destroy$))
        .subscribe(shouldPreserve => {
          if (shouldPreserve) {
            // Ensure Project Synapse is unfolded when preservation is requested
            setTimeout(() => {
              this.isProjectSynapseFolded = false;
              this.isAiFolded = true;
              this.isMetadataFolded = true;
              this.isStylometricsFolded = true;
              this.cdr.detectChanges(); // Force immediate update
            }, 10);
          }
        })
    );

    // Add this subscription to detect when sentence information is cleared
    this.subscriptions.push(
      this.reportService.sentenceInformationObject.pipe(
        takeUntil(this.destroy$)
      ).subscribe(sentenceInfo => {
        // If sentence info is cleared (null) and Project Synapse is active
        if (!sentenceInfo && !this.isProjectSynapseFolded) {

          // Check if we should preserve Project Synapse state
          this.reportService.preserveProjectSynapseState$.pipe(
            take(1)
          ).subscribe(shouldPreserve => {
            if (shouldPreserve) {
              // Keep Project Synapse open with a small delay to ensure proper sequence
              setTimeout(() => this.keepProjectSynapseOpen(), 50);
            }
          });
        }
      })
    );

    this.subscriptions.push(
      this.stylometricState.stylometricFlags$.subscribe((flags: number) => {
        if (this.stylometricMetrics && this.metricsThresholds && this.previousSubmissions?.all) {
          this.calculateStylometricFlags();
        }
      })
    );
  }

  getAiRiskLevelClass(
    value: number | null | undefined,
    useFixedThresholds: boolean = false
  ): 'low' | 'medium' | 'high' {
    if (value == null) return 'low';

    if (useFixedThresholds) {
      if (value < 15) return 'low';
      if (value < 35) return 'medium';
      return 'high';
    } else {
      if (value < this.mediumRiskThreshold) return 'low';
      if (value < this.highRiskThreshold) return 'medium';
      return 'high';
    }
  }

  getAiRiskLabel(value: number | null | undefined): string {
    if (value == null) return 'new_entries.low_probability';
    if (value < 15) return 'new_entries.low_probability';
    if (value < 35) return 'new_entries.moderate_probability';
    return 'new_entries.high_probability';
  }

  getAiRiskLevel(): 'low' | 'medium' | 'high' {
    const percentage = this.aiAnalysis?.aiPercentage || 0;

    if (percentage >= 35) return 'high';
    if (percentage >= 15) return 'medium';
    return 'low';
  }


  getAiAuthorshipLabel(): string {
    const percentage = this.aiAnalysis?.aiPercentage || 0;

    if (percentage >= 35) {
      return 'new_entries.highly_likely_ai';
    }
    if (percentage >= 15) {
      return 'new_entries.possibly_ai';
    }
    return 'new_entries.unlikely_ai';
  }

  // getAiRiskLabel(): string {
  //   const percentage = this.aiAnalysis?.aiPercentage || 0;

  //   if (percentage >= this.highRiskThreshold) return 'High Risk';
  //   if (percentage >= this.mediumRiskThreshold) return 'Medium Risk';
  //   return 'Low Risk';
  // }

  @HostListener('document:keydown.escape', ['$event'])
  onInsightTooltipEscape(event: KeyboardEvent): void {
    const openContainers = this.host.nativeElement.querySelectorAll(
      '.tooltip-container:hover, .tooltip-container:focus-within'
    );

    if (!openContainers.length) {
      return;
    }

    openContainers.forEach((el) => el.classList.add('tooltip-dismissed'));
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('mouseout', ['$event'])
  onTooltipMouseOut(event: MouseEvent): void {
    this.clearTooltipDismiss(event.target as Element | null, event.relatedTarget as Node | null);
  }

  @HostListener('focusout', ['$event'])
  onTooltipFocusOut(event: FocusEvent): void {
    this.clearTooltipDismiss(event.target as Element | null, event.relatedTarget as Node | null);
  }

  private clearTooltipDismiss(target: Element | null, relatedTarget: Node | null): void {
    const container = target?.closest?.('.tooltip-container');
    if (!container || !this.host.nativeElement.contains(container)) {
      return;
    }
    if (relatedTarget && container.contains(relatedTarget)) {
      return;
    }
    container.classList.remove('tooltip-dismissed');
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  setAiPercentageTranslations(): void {
    // Get all translations in one operation to reduce subscription overhead
    const translationKeys = [
      'report.this_text_has_a_high_probability_of_being_ai_generated',
      'report.this_text_has_a_moderate_probability_of_being_ai_generated',
      'report.this_text_has_a_low_probability_of_being_ai_generated'
    ];

    translationKeys.forEach(key => {
      this.translate.get(key).pipe(take(1)).subscribe((res: string) => {
        if (key.includes('high')) this.ai_percentage.high = res;
        else if (key.includes('moderate')) this.ai_percentage.medium = res;
        else if (key.includes('low')) this.ai_percentage.low = res;
      });
    });
  }

  private initializeFoldStates(): void {
    // Only set analysis type if we're not in summary view
    if (!this.isOnAi) {
      return;
    }

    // Keep all sections folded by default
    this.isAiFolded = true;
    this.isMetadataFolded = true;
    this.isStylometricsFolded = true;
    this.isProjectSynapseFolded = true;

    const wasProjectSynapseActive = this.stylometricState.getProjectSynapseActive();

    // If Project Synapse was active, keep it active and show it first
    if (wasProjectSynapseActive) {
      this.isProjectSynapseFolded = false;
      this.stylometricState.forceProjectSynapseState(true);
      this.reportService.selectAnalysisTypeSubject.next('projectSynapse');
      return;
    }

    if (this.simpleAIEnable) {
      this.isAiFolded = false;
    } else if (this.stylometricsEnable) {
      this.isStylometricsFolded = false;
    } else if (this.projectSynapseEnable) {
      this.isProjectSynapseFolded = false;
    } else if (this.metadataEnable) {
      this.isMetadataFolded = false;
    }

    // Force change detection
    this.cdr.markForCheck();
  }

  toggleIsOnAi(): void {
    // If we're in stylometric view and AI is clicked, switch back to classic view
    this.stylometricState.reportType$
      .pipe(take(1)) // This ensures the subscription completes after one emission
      .subscribe(type => {
        if (type === 'stylometric') {
          this.stylometricState.setReportType('classic');
          return;
        }

        this.isOnAi = !this.isOnAi;
        this.isOnAiChange.emit(this.isOnAi);
        this.documentDataService.setCurrentPage(1);

        if (this.isOnAi) {
          // Show the first available analysis
          this.initializeFoldStates();
          this.countSentences();

          setTimeout(() => {
            this.reportService.selectAnalysisTypeSubject.next('ai');
          }, 50);
        } else {
          // Ensure Project Synapse is disabled when leaving AI view
          this.isProjectSynapseFolded = true;
          if (this.stylometricState.getProjectSynapseActive()) {
            this.stylometricState.toggleProjectSynapseActive(false);
          }
          this.reportService.selectAnalysisTypeSubject.next('none');
        }

        setTimeout(() => this.sectionHeading?.nativeElement?.focus({ preventScroll: true }), 0);
      });
  }

  countSentences() {
    this.totalSentences = 0;
    this.aiSentences = 0;
    this.humanSentences = 0;

    if (this.totalAIStenteces && Array.isArray(this.totalAIStenteces)) {
      this.totalAIStenteces.forEach((sentence: any) => {
        this.totalSentences++;

        const isAiFromPangram = sentence.pangramResults?.aiText;
        const isAiLegacy = sentence.aiText?.aiText;
        const isAiFromBoolean = typeof sentence.aiText === 'boolean' ? sentence.aiText : false;

        const combinedAiFlag = !!(isAiFromPangram ?? isAiLegacy ?? isAiFromBoolean);

        if (combinedAiFlag) {
          this.aiSentences++;
        }
      });
    }

    // Prefer the more accurate allSentencesLength when available; otherwise fall back to totalSentences
    const totalBase = (this.allSentencesLength && this.allSentencesLength > 0)
      ? this.allSentencesLength
      : this.totalSentences;

    this.humanSentences = totalBase - this.aiSentences;
  }


  toggleAiFold(): void {
    this.isAiFolded = !this.isAiFolded;
    if (!this.isAiFolded) {
      this.isMetadataFolded = true;
      this.isStylometricsFolded = true;
      this.isProjectSynapseFolded = true;

      // Ensure Project Synapse view is disabled only if it's currently active
      if (this.stylometricState.getProjectSynapseActive()) {
        this.stylometricState.toggleProjectSynapseActive(false);
      }

      // Update analysis type
      this.reportService.selectAnalysisTypeSubject.next('ai');
    }
    this.cdr.markForCheck();
  }

  toggleMetadataFold(): void {

    // Toggle the fold state
    this.isMetadataFolded = !this.isMetadataFolded;

    if (!this.isMetadataFolded) {
      // If we're unfolding metadata, fold other sections
      this.isAiFolded = true;
      this.isStylometricsFolded = true;
      this.isProjectSynapseFolded = true;

      // Set the analysis type to metadata
      this.reportService.selectAnalysisTypeSubject.next('metadata');

      // Ensure we're in classic mode
      this.stylometricState.setReportType('classic');
      this.stylometricState.forceProjectSynapseState(false);

      // Ensure Project Synapse view is disabled only if it's currently active
      if (this.stylometricState.getProjectSynapseActive()) {

        this.stylometricState.toggleProjectSynapseActive(false);
      }

      // Force a UI update
      this.cdr.markForCheck();

      // Double-check the fold state after a small delay
      setTimeout(() => {
        this.isMetadataFolded = false;
        this.cdr.markForCheck();
      }, 50);
    } else {
      // Just update the UI if we're folding
      this.cdr.markForCheck();
    }
  }

  toggleStylometricsFold(): void {
    this.isStylometricsFolded = !this.isStylometricsFolded;
    if (!this.isStylometricsFolded) {
      this.isAiFolded = true;
      this.isMetadataFolded = true;
      this.isProjectSynapseFolded = true;

      // Ensure Project Synapse view is disabled only if it's currently active
      if (this.stylometricState.getProjectSynapseActive()) {
        this.stylometricState.toggleProjectSynapseActive(false);
      }

      // Update analysis type
      this.reportService.selectAnalysisTypeSubject.next('stylometrics');
    }
    this.cdr.markForCheck();
  }

  toggleProjectSynapseFold(): void {
    this.isProjectSynapseFolded = !this.isProjectSynapseFolded;

    // Always explicitly set the Project Synapse state based on the fold state
    // This ensures the state is correctly synchronized
    if (!this.isProjectSynapseFolded) {
      // When opening Project Synapse, use forceProjectSynapseState to ensure it's activated
      this.stylometricState.forceProjectSynapseState(true);
    } else {
      // When closing Project Synapse, explicitly deactivate it
      this.stylometricState.forceProjectSynapseState(false);
    }

    if (!this.isProjectSynapseFolded) {
      // When opening Project Synapse, close other sections
      this.isAiFolded = true;
      this.isMetadataFolded = true;
      this.isStylometricsFolded = true;

      // Notify the report service about the analysis type change
      this.reportService.selectAnalysisTypeSubject.next('projectSynapse');
    } else {
      // When closing Project Synapse, default to AI analysis
      this.isAiFolded = false;
      this.reportService.selectAnalysisTypeSubject.next('ai');
    }

    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["aiAnalysis"] && !changes["aiAnalysis"].firstChange) {
      this.initializeFoldStates();
    }
    if (changes["isOnAi"]) {
      this.countSentences();
      // When returning to AI view, sync Project Synapse state
      if (this.isOnAi) {
        // Use synchronous method to get current Project Synapse state
        const isProjectSynapseActive = this.stylometricState.getProjectSynapseActive();

        this.isProjectSynapseFolded = !isProjectSynapseActive;

        if (isProjectSynapseActive) {
          // If Project Synapse was active, reactivate it using forceProjectSynapseState
          this.stylometricState.forceProjectSynapseState(true);
          // Ensure the analysis type is set to projectSynapse
          setTimeout(() => {
            this.reportService.selectAnalysisTypeSubject.next('projectSynapse');
          }, 50);
        } else {
          // Initialize fold states based on available analyses
          setTimeout(() => {
            const currentType = this.reportService.selectAnalysisTypeSubject.getValue();
            if (currentType === 'none') {
              this.initializeFoldStates();
              setTimeout(() => {
                this.reportService.selectAnalysisTypeSubject.next('ai');
              }, 50);
            }
          }, 0);
        }
        // Force change detection
        this.cdr.markForCheck();
      } else {
        // Reset state when leaving AI view
        this.isProjectSynapseFolded = true;
        // Use forceProjectSynapseState to ensure it's deactivated
        this.stylometricState.forceProjectSynapseState(false);
      }
    }
    if (changes["metadata"]) {
      const tempMetadata = { ...this.metadata }; // Create a shallow copy of metadata
      delete tempMetadata.flags; // Remove flags
      delete tempMetadata.path; // Remove path
      delete tempMetadata.llm_analysis; // Remove llm_analysis
      delete tempMetadata.excluded_flags; // Remove excluded_flags
      this.metadataLength = Object.keys(tempMetadata).length || 0; // Update metadataLength
    }

    if (changes["metadata"] || changes["anonymous"]) {
      this.recalculateMetadataIssues();
    }
    if ((changes["stylometricMetrics"] || changes["metricsThresholds"] || changes["previousSubmissions"]) &&
      this.stylometricMetrics && this.metricsThresholds && this.previousSubmissions?.all) {
      this.calculateStylometricFlags();
    }
    if (changes["projectSynapseData"] && this.projectSynapseData?.length > 0) {
      this.updateMostProbableModel();
    }

    // Ensure sentence counts are recalculated whenever the underlying data or totals change,
    // so they are correct already on initial load (not only when toggling AI in-depth view).
    if (changes["totalAIStenteces"] || changes["allSentencesLength"]) {
      this.countSentences();
    }
  }

  updateMetadataIssues(activeFlags: number): void {
    this.metadataIssues = this.metadata ? activeFlags : 0;
  }

  onMetadataRefreshRequested() {
    if (this.metadata) {
      this.metadataRefreshRequested.emit();
      this.cdr.markForCheck();
    }
  }

  onFlagsChanged(activeFlags: number): void {
    if (this.metadata) {
      this.metadataIssues = activeFlags;
      this.cdr.markForCheck();
    }
  }

  getMetricsCount() {
    if (!this.stylometricMetrics) return 0;

    // If we've already calculated flags, return that count
    if (this.stylometricFlagsCount > 0) {
      return this.stylometricFlagsCount;
    }

    // Otherwise return the total number of metrics
    return Object.values(this.stylometricMetrics).reduce((total: any, category: any[]) => {
      return (total + category.length);
    }, 0);
  }

  /**
   * Sets the active view state in the AI bar
   * @param targetState The analysis type to activate
   */
  setViewState(targetState: AnalysisType): void {
    // Check if we're currently in Project Synapse view
    const wasInProjectSynapse = !this.isProjectSynapseFolded;

    // If we're switching from AI mode to another mode
    if (this.isOnAi === false) {
      // Toggle AI mode on
      this.isOnAi = true;
      this.isOnAiChange.emit(true);

      // Reset all sections to folded
      this.isAiFolded = true;
      this.isMetadataFolded = true;
      this.isStylometricsFolded = true;
      this.isProjectSynapseFolded = true;

      // Then unfold the target section
      if (targetState === 'ai') {
        this.isAiFolded = false;
        // Ensure Project Synapse is deactivated
        this.stylometricState.forceProjectSynapseState(false);
        setTimeout(() => {
          this.reportService.selectAnalysisTypeSubject.next('ai');
        }, 50);
      } else if (targetState === 'metadata') {
        this.isMetadataFolded = false;

        // First set the analysis type
        this.reportService.selectAnalysisTypeSubject.next('metadata');

        // Ensure we're in classic mode for metadata
        this.stylometricState.setReportType('classic');

        // Force the report type to be 'classic' again after a small delay
        setTimeout(() => {
          // Double-check that metadata is still unfolded
          this.isMetadataFolded = false;

          // Ensure we're still in classic mode
          this.stylometricState.setReportType('classic');

          // Ensure Project Synapse is deactivated
          this.stylometricState.forceProjectSynapseState(false);

          // Force a UI update
          this.cdr.markForCheck();
        }, 50);
      } else if (targetState === 'stylometrics') {
        this.isStylometricsFolded = false;
        this.reportService.selectAnalysisTypeSubject.next('stylometrics');
        // Ensure Project Synapse is deactivated
        this.stylometricState.forceProjectSynapseState(false);
      } else if (targetState === 'projectSynapse') {
        this.isProjectSynapseFolded = false;
        // Always force Project Synapse to be active when explicitly navigating to it
        this.stylometricState.forceProjectSynapseState(true);
        this.reportService.selectAnalysisTypeSubject.next('projectSynapse');
      }
    } else {
      // Already in AI mode, just toggle the correct section
      this.isAiFolded = targetState === 'ai' ? false : true;
      this.isMetadataFolded = targetState === 'metadata' ? false : true;
      this.isStylometricsFolded = targetState === 'stylometrics' ? false : true;
      this.isProjectSynapseFolded = targetState === 'projectSynapse' ? false : true;

      // Special handling for metadata to ensure classic mode
      if (targetState === 'metadata') {
        // Ensure metadata is unfolded and other sections are folded
        this.isAiFolded = true;
        this.isMetadataFolded = false;
        this.isStylometricsFolded = true;
        this.isProjectSynapseFolded = true;

        // Ensure we're in classic mode for metadata
        this.stylometricState.setReportType('classic');

        // Update the analysis type
        this.reportService.selectAnalysisTypeSubject.next('metadata');

        // Force the report type to be 'classic' again after a small delay
        setTimeout(() => {
          this.stylometricState.setReportType('classic');
          this.cdr.markForCheck();
        }, 50);
      }
      // Update Project Synapse state if needed
      else if (targetState === 'projectSynapse') {
        // Always explicitly activate Project Synapse when navigating to it
        this.stylometricState.forceProjectSynapseState(true);

        // Ensure the analysis type is set to projectSynapse
        setTimeout(() => {
          this.reportService.selectAnalysisTypeSubject.next('projectSynapse');
        }, 0);
      } else {
        if (targetState === 'ai') {
          setTimeout(() => {
            this.reportService.selectAnalysisTypeSubject.next('ai');
          }, 50);
        } else {
          this.reportService.selectAnalysisTypeSubject.next(targetState);
        }

        // Then deactivate Project Synapse if it was active
        if (wasInProjectSynapse) {
          // Small delay to ensure the analysis type change happens first
          setTimeout(() => {
            this.stylometricState.forceProjectSynapseState(false);
          }, 10);
        }
      }
    }

    this.cdr.markForCheck();
  }

  /**
   * Explicitly clears Project Synapse highlights by deactivating the state and triggering a full cleanup
   */
  private clearProjectSynapseHighlights(): void {
    // Check if we're in the middle of clearing sentence info
    const sentenceInfoBeingCleared = !this.reportService.sentenceInformationSubject.getValue() &&
      !this.isProjectSynapseFolded;

    // If we're clearing sentence info and Project Synapse is active, don't clear highlights
    if (sentenceInfoBeingCleared) {
      return; // Skip the rest of this method
    }

    // Explicitly deactivate Project Synapse
    this.stylometricState.forceProjectSynapseState(false);

    // This causes a re-render of the document content
    // Use a temporary 'none' state to force a refresh
    this.reportService.selectAnalysisTypeSubject.next('none');

    // Reset document view by changing the page and then returning to it
    // This forces a re-render without needing a dedicated refresh method
    const currentPage = this.documentDataService.getCurrentPage().pipe(take(1));
    currentPage.subscribe(page => {
      // First change to a different page (or just set to 1 temporarily)
      this.documentDataService.setCurrentPage(page === 1 ? 2 : 1);

      // Then after a small delay, restore the original page
      setTimeout(() => {
        this.documentDataService.setCurrentPage(page);
      }, 50);
    });
  }

  // Optimized navigation methods
  navigateToStylometrics(): void {
    this.setViewState('stylometrics');
  }

  navigateToAiAnalysis(): void {
    this.navigateToAi(); // Use the same robust implementation
  }

  navigateToMetadata(): void {
    // Ensure we're in AI mode
    if (!this.isOnAi) {
      this.isOnAi = true;
      this.isOnAiChange.emit(true);
    }

    // First ensure we're in classic mode - but don't call setReportType as it has side effects
    // this.stylometricState.setReportType('classic');

    // Instead, directly set the report type without side effects
    this.stylometricState.forceProjectSynapseState(false);

    // Reset all fold states
    this.isAiFolded = true;
    this.isStylometricsFolded = true;
    this.isProjectSynapseFolded = true;
    this.isMetadataFolded = false;

    // Set the analysis type to metadata
    this.reportService.selectAnalysisTypeSubject.next('metadata');

    // Force a UI update
    this.cdr.markForCheck();

    // Double-check the fold states after a small delay
    setTimeout(() => {
      // Double-check the fold states
      this.isAiFolded = true;
      this.isStylometricsFolded = true;
      this.isProjectSynapseFolded = true;
      this.isMetadataFolded = false;

      // Ensure the analysis type is still metadata
      this.reportService.selectAnalysisTypeSubject.next('metadata');

      // Force another UI update
      this.cdr.markForCheck();
    }, 50);
  }

  navigateToProjectSynapse(): void {
    // Use setViewState to ensure consistent state management
    this.setViewState('projectSynapse');

    // Always explicitly activate Project Synapse when navigating to it
    // Use forceProjectSynapseState to ensure it's activated regardless of current state
    this.stylometricState.forceProjectSynapseState(true);

    // Ensure the fold state matches
    this.isProjectSynapseFolded = false;

    // Ensure the analysis type is set to projectSynapse
    this.reportService.selectAnalysisTypeSubject.next('projectSynapse');

    // Force change detection
    this.cdr.detectChanges();
  }

  // Add this new method to the AiBar class
  keepProjectSynapseOpen(): void {

    // Ensure Project Synapse stays unfolded
    this.isProjectSynapseFolded = false;

    // Make sure other sections are folded
    this.isAiFolded = true;
    this.isMetadataFolded = true;
    this.isStylometricsFolded = true;

    // Ensure Project Synapse state is active using forceProjectSynapseState
    this.stylometricState.forceProjectSynapseState(true);

    // Ensure the analysis type is set to projectSynapse
    this.reportService.selectAnalysisTypeSubject.next('projectSynapse');

    // Force UI update
    this.cdr.detectChanges();

    // Add a delayed check to ensure Project Synapse stays active
    setTimeout(() => {
      if (this.stylometricState.getProjectSynapseActive() === false) {
        this.stylometricState.forceProjectSynapseState(true);
        this.reportService.selectAnalysisTypeSubject.next('projectSynapse');
        this.cdr.detectChanges();
      }
    }, 300);
  }

  /**
   * Handles changes to the Project Synapse stylistic discrepancy level
   * @param discrepancyLevel The new discrepancy level ('low', 'medium', or 'high')
   */
  onProjectSynapseDiscrepancyChange(discrepancyLevel: string): void {
    this.projectSynapseStylisticDiscrepancy = discrepancyLevel;

    // You can add additional logic here to update UI based on the discrepancy level
    this.cdr.markForCheck();
  }

  /**
   * Calculates the number of stylometric flags based on comparing current metrics with previous submissions
   * and checking if the difference exceeds the threshold
   */
  calculateStylometricFlags(): void {
    if (!this.stylometricMetrics || !this.metricsThresholds || !this.previousSubmissions?.all) {
      this.stylometricFlagsCount = 0;
      return;
    }

    let flagsCount = 0;
    const categories = ['lexical', 'readability', 'structural', 'syntactic'];

    categories.forEach(category => {
      if (this.stylometricMetrics[category] && this.previousSubmissions.all[category]) {
        this.stylometricMetrics[category].forEach((currentMetric: any) => {
          const metricName = currentMetric.name;
          const currentValue = currentMetric.value;

          // Find the corresponding metric in previous submissions
          const previousMetric = this.previousSubmissions.all[category].find(
            (m: any) => m.name === metricName
          );

          if (previousMetric && this.metricsThresholds[metricName]) {
            const previousValue = previousMetric.value;
            const threshold = this.metricsThresholds[metricName];

            // Flag if the absolute difference exceeds the threshold
            if (Math.abs(currentValue - previousValue) > threshold) {
              flagsCount++;
            }
          }
        });
      }
    });

    this.stylometricFlagsCount = flagsCount;
    this.cdr.markForCheck();
  }

  /**
   * Updates the most probable model by sorting the projectSynapseData by AI percentage
   */
  private updateMostProbableModel(): void {
    if (!this.projectSynapseData || this.projectSynapseData.length === 0) {
      this.mostProbableModel = null;
      return;
    }

    // Sort by AI percentage in descending order and take the highest
    const sortedData = [...this.projectSynapseData].sort((a, b) => {
      const percentageA = a.comparisonData?.ai_percentage || 0;
      const percentageB = b.comparisonData?.ai_percentage || 0;
      return percentageB - percentageA; // Descending order (highest first)
    });

    this.mostProbableModel = sortedData[0];
  }

  /**
   * Gets the status text for the most probable model
   */
  getMostProbableModelStatus(): string {
    if (!this.mostProbableModel) {
      return 'Unknown';
    }

    const percentage = this.mostProbableModel.comparisonData?.ai_percentage || 0;

    if (percentage <= 20) {
      return 'Low';
    } else if (percentage > 20 && percentage <= 80) {
      return 'Moderate';
    } else {
      return 'High';
    }
  }

  /**
   * Gets the CSS class for the most probable model
   */
  getMostProbableModelClass(): any {
    if (!this.mostProbableModel) {
      return { 'low': true };
    }

    const percentage = this.mostProbableModel.comparisonData?.ai_percentage || 0;

    return {
      'low': percentage <= 20,
      'medium': percentage > 20 && percentage <= 80,
      'high': percentage > 80
    };
  }

  /**
   * Gets the formatted percentage for the most probable model
   */
  getMostProbableModelPercentage(): string {
    if (!this.mostProbableModel) {
      return '0.00';
    }

    const percentage = this.mostProbableModel.comparisonData?.ai_percentage || 0;
    return percentage.toFixed(2);
  }

  /**
   * Navigate to AI authorship analysis section
   */
  navigateToAi(): void {
    if (!this.isOnAi) {
      this.isOnAi = true;
      this.isOnAiChange.emit(true);
    }

    this.reportService.selectAnalysisTypeSubject.next('ai');

    // Reset all fold states first to avoid conflicts
    this.isMetadataFolded = true;
    this.isStylometricsFolded = true;
    this.isProjectSynapseFolded = true;

    // Explicitly set AI to unfolded
    this.isAiFolded = false;

    // Explicitly make sure Project Synapse is deactivated
    this.stylometricState.forceProjectSynapseState(false);

    // Force change detection
    this.cdr.detectChanges();

    // Double-check the fold states after a small delay
    setTimeout(() => {
      this.isAiFolded = false;
      this.isMetadataFolded = true;
      this.isStylometricsFolded = true;
      this.isProjectSynapseFolded = true;

      // Ensure the analysis type is still AI
      this.reportService.selectAnalysisTypeSubject.next('ai');

      // Force another UI update
      this.cdr.detectChanges();
    }, 50);
  }

  getAIConfidenceScore(): string {
    if (!this.pangramResults || !this.pangramResults.prediction_short) {
      return 'N/A';
    }
    if (this.pangramResults.prediction_short === 'AI') { return 'High' }
    return this.pangramResults.prediction_short;
  }

  getAISegmentsCount(): number {
    if (!this.pangramResults || !this.pangramResults.ai_sentences || !Array.isArray(this.pangramResults.ai_sentences)) {
      return 0;
    }
    return this.pangramResults.ai_sentences.filter((segment: any) => segment.label === 'AI-Generated').length;
  }

  getAIAssistedScore(): string {
    if (!this.pangramResults || this.pangramResults.fraction_ai === undefined) {
      return '0%';
    }
    return (this.pangramResults.fraction_ai * 100).toFixed(1) + '%';
  }

  getHumanScore(): string {
    if (!this.pangramResults || this.pangramResults.fraction_human === undefined) {
      return '0%';
    }
    return (this.pangramResults.fraction_human * 100).toFixed(1) + '%';
  }

  getHumanSegmentsCount(): number {
    if (!this.pangramResults || !this.pangramResults.ai_sentences || !Array.isArray(this.pangramResults.ai_sentences)) {
      return 0;
    }
    return this.pangramResults.ai_sentences.filter((segment: any) => segment.label === 'Human Written').length;
  }
}
